import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, Vote, LogOut, LogIn, Check, Star, Crown, Sparkles, Palette, Image, Shield, Smartphone, Globe, UserCheck, Zap, Quote } from "lucide-react";
import { CreateEventForm } from "@/components/CreateEventForm";
import NavigationBar from "@/components/NavigationBar";
import FeatureRequestDialog from "@/components/FeatureRequestDialog";
import { EmailCapture } from '@/components/EmailCapture';
import { SubscriptionPlans } from '@/components/SubscriptionPlans';
import { Event } from "@/types/event";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
const Index = () => {
  const [currentView, setCurrentView] = useState<'home' | 'create'>('home');
  const {
    user,
    signOut
  } = useAuth();
  const {
    subscription
  } = useSubscription();
  const navigate = useNavigate();
  const handleSignOut = async () => {
    await signOut();
  };
  const handleEventCreated = (event: Event) => {
    // After creating an event, redirect to dashboard or show success message
    navigate('/dashboard');
  };
  const renderCurrentView = () => {
    switch (currentView) {
      case 'create':
        return <CreateEventForm onEventCreated={handleEventCreated} onBack={() => setCurrentView('home')} />;
      default:
        return <div className="min-h-screen bg-gradient-background relative overflow-hidden">
            {/* Top navigation bar with auth button */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20">
              {user ? <Button variant="ghost" size="sm" onClick={handleSignOut} className="flex items-center gap-2 min-h-[44px]">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Button> : <Button variant="glass" size="sm" onClick={() => navigate('/auth')} className="flex items-center gap-2 min-h-[44px]">
                  <LogIn className="h-4 w-4" />
                  <span className="hidden xs:inline">Sign In</span>
                </Button>}
            </div>

            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-primary rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-success rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-hero rounded-full mix-blend-multiply filter blur-2xl opacity-10 animate-pulse delay-2000"></div>
            </div>

            {/* Hero Section */}
            <div className="container mx-auto px-4 py-20 relative z-10">
              <div className="text-center mb-20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-hero rounded-2xl mb-8 shadow-elevated hover:scale-110 transition-all duration-500">
                  <img src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" alt="EventDatePicker - Plan Together, Decide Together" className="w-14 h-14 object-contain" loading="lazy" />
                </div>
                
                <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent mb-6 leading-tight">
                  <strong>Find a Date That Works for Everyone</strong> with Our <strong>Group Scheduling Tool</strong>
                </h1>
                
                <p className="hero-text">
                  The best <strong>meeting time finder</strong> and <strong>availability poll</strong> platform. <strong>Create a poll to pick a date</strong> with our <strong>online tool to coordinate availability</strong> for all your scheduling needs.
                </p>
                
                <div className="mb-8 max-w-2xl mx-auto">
                  <div className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-accent-foreground bg-slate-800">
                    <Sparkles className="w-4 h-4" />
                    Plus, generate stunning AI-powered invitation images with Pro!
                  </div>
                </div>

                {/* Social Proof */}
                <div className="mb-12 max-w-2xl mx-auto text-center">
                  <div className="flex flex-wrap justify-center gap-6 items-center text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-success" />
                      <span>Loved by event organizers and casual planners alike</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-success" />
                      <span>Thousands of events planned worldwide </span>
                    </div>
                    
                  </div>
                </div>
                
                <div className="flex flex-col items-center gap-3">
                  <div className="flex flex-col sm:flex-row gap-3 justify-center w-full sm:w-auto">
                    {user ? (
                      <>
                        <Button variant="hero" size="xl" onClick={() => navigate('/create-event')} className="text-lg px-12 py-6 rounded-2xl shadow-elevated hover:shadow-glow transition-all">
                          <Calendar className="w-5 h-5 mr-2" />
                          Create Your Event Now
                        </Button>
                        <Button variant="glass" size="xl" onClick={() => navigate('/dashboard')} className="text-lg px-12 py-6 rounded-2xl bg-primary/10 hover:bg-primary/20 transition-colors">
                          Go to Dashboard
                        </Button>
                      </>
                    ) : (
                      <Button variant="hero" size="xl" onClick={() => navigate('/auth?next=/create-event')} className="text-lg px-12 py-6 rounded-2xl shadow-elevated hover:shadow-glow transition-all">
                        <Calendar className="w-5 h-5 mr-2" />
                        Create Your First Event — Free
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No signup needed for guests · Free for up to 3 events/month
                  </p>
                  {!user && (
                    <button
                      type="button"
                      onClick={() => navigate('/auth?next=/create-event')}
                      className="text-sm text-primary hover:underline font-medium min-h-[44px]"
                    >
                      Already have an account? Sign in
                    </button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate('/event-planner-pro')} className="text-sm text-muted-foreground hover:text-primary mt-2">
                    <Crown className="w-4 h-4 mr-2" />
                    For event planners — explore Pro
                  </Button>
                </div>

                {/* How it works — 3-step strip */}
                <div className="mt-12 max-w-4xl mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { n: 1, title: "Create event", desc: "Add dates, times, or locations" },
                      { n: 2, title: "Share the link", desc: "Email, text, or paste anywhere" },
                      { n: 3, title: "See best option win", desc: "Real-time results, no chasing" },
                    ].map((s) => (
                      <button
                        key={s.n}
                        type="button"
                        onClick={() => navigate(user ? '/create-event' : '/auth?next=/create-event')}
                        className="text-left p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/40 hover:shadow-medium transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
                            {s.n}
                          </span>
                          <div>
                            <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">{s.title}</h3>
                            <p className="text-xs text-muted-foreground">{s.desc}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
                <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                  <CardHeader className="pb-4">
                    <div className="w-16 h-16 bg-gradient-primary rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-medium group-hover:scale-110 transition-all duration-500">
                      <Users className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <CardTitle className="text-xl mb-2">Easy Collaboration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        <span>Email, phone, or shareable link </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        <span>No signup required for guests</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        <span>Instant collaboration</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                  <CardHeader className="pb-4">
                    <div className="w-16 h-16 bg-gradient-success rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-medium group-hover:scale-110 transition-all duration-500">
                      <Vote className="w-8 h-8 text-accent-foreground" />
                    </div>
                    <CardTitle className="text-xl mb-2">Smart Voting</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                        <span>Vote on dates, times, and locations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                        <span>Real-time results</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                        <span>Set a countdown timers</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group relative">
                  <CardHeader className="pb-4">
                    <div className="w-16 h-16 bg-gradient-hero rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-medium group-hover:scale-110 transition-all duration-500">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl mb-2">
                      AI Image Generation
                      <Badge variant="outline" className="ml-2 text-xs">
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                        <span>Create a stunning invitation images</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                        <span>Multiple styles and formats</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                        <span>AI-powered personalization</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                  <CardHeader className="pb-4">
                    <div className="w-16 h-16 bg-warning rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-medium group-hover:scale-110 transition-all duration-500">
                      <Clock className="w-8 h-8 text-warning-foreground" />
                    </div>
                    <CardTitle className="text-xl mb-2">Perfect Timing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-warning rounded-full"></span>
                        <span>Set voting deadlines</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-warning rounded-full"></span>
                        <span>Automatic best option selection</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-warning rounded-full"></span>
                        <span>Majority preference algorithm</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Use Cases Section */}
              <div className="text-center mb-20">
                <h2 className="text-4xl font-bold mb-4 bg-gradient-hero bg-clip-text text-transparent">Perfect for Every Occasion - <strong>Schedule Group Events</strong> with Ease</h2>
                <p className="text-xl text-muted-foreground mb-16 max-w-2xl mx-auto">
                  From <strong>organizing family gatherings</strong> to <strong>planning birthday party dates</strong>, our <strong>free scheduling tool</strong> makes <strong>event date coordination</strong> effortless
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                  {/* Family Reunion */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=450&q=80" 
                        alt="Family gathering around dinner table"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <h3 className="text-xl text-amber-600 mb-2 font-semibold">Organize Family Gatherings</h3>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        <strong>Choose wedding dates with family</strong> and <strong>plan birthday party dates</strong> with ease! Our <strong>simple date picker for events</strong> helps <strong>organize family gatherings</strong> across different time zones.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Sports Event */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=450&q=80" 
                        alt="Basketball on outdoor court"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl text-orange-600 mb-2">Pickup Basketball</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        Ready to <strong>choose a date with friends</strong>? Use our <strong>calendar poll</strong> to find the perfect time for outdoor activities and sports events.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Business Meeting */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=450&q=80" 
                        alt="Professional business meeting"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <h3 className="text-xl text-blue-600 mb-2 font-semibold">Team Meeting Organizer</h3>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        <strong>Organize team meetings</strong> and corporate events efficiently. Learn <strong>how to schedule an event with multiple people</strong> using our <strong>tools to find meeting times</strong> that work for everyone.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Book Club */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=450&q=80" 
                        alt="Open book with coffee"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl text-emerald-600 mb-2">Book Club</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        Perfect <strong>club meeting scheduler</strong> for book clubs, hobby groups, and community organizations. <strong>Share your calendar to pick dates</strong> that work for everyone.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Weekend BBQ */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=450&q=80" 
                        alt="Friends enjoying outdoor BBQ"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl text-red-600 mb-2">Weekend BBQ</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        Use our <strong>app to find the best date for your event</strong>. Perfect for social gatherings and community events with our <strong>poll for best date</strong> feature.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Night Out */}
                  <Card className="text-center hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1470813740244-df37b8c1edcb?auto=format&fit=crop&w=450&q=80" 
                        alt="Beautiful starry night sky"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl text-indigo-600 mb-2">Night Out</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed">
                        Discover <strong>how to schedule an event with multiple people</strong> for social activities. Our <strong>date coordination app</strong> makes planning nights out effortless.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Benefits You'll Love Section */}
              <div className="text-center mb-20">
                <h2 className="text-4xl font-bold mb-4 bg-gradient-hero bg-clip-text text-transparent">Benefits You'll Love</h2>
                <p className="text-xl text-muted-foreground mb-16 max-w-2xl mx-auto">
                  Everything you need to make event planning effortless and enjoyable
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                          <Clock className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Save Time</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        No more back-and-forth emails or messages trying to coordinate schedules.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                          <Shield className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Stay Organized</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        Keep all event details, votes, and communications in one place.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                          <Smartphone className="w-6 h-6 text-purple-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Mobile Friendly</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        Access your events and respond to invites from any device.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                          <Globe className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Work Globally</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        Perfect for coordinating across different time zones and locations.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                          <UserCheck className="w-6 h-6 text-yellow-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Group Consensus</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        Make decisions that work for everyone with democratic voting.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center">
                          <Zap className="w-6 h-6 text-indigo-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Real-time Updates</CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        Get instant notifications when people vote or plans change.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Pricing Section */}
              <SubscriptionPlans />

              {/* User Testimonials */}
              <div className="text-center mb-20">
                <h2 className="text-4xl font-bold mb-4 bg-gradient-hero bg-clip-text text-transparent">What Our Users Say</h2>
                <p className="text-xl text-muted-foreground mb-16 max-w-2xl mx-auto">
                  Join thousands of happy users who've simplified their event planning
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">SJ</span>
                        </div>
                        <div>
                          <h4 className="font-semibold">Sarah Johnson</h4>
                          <p className="text-sm text-muted-foreground">Event Organizer</p>
                        </div>
                      </div>
                      <div className="flex mb-3">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Quote className="w-6 h-6 text-muted-foreground mb-3" />
                      <p className="text-base leading-relaxed mb-4">
                        "This app has revolutionized how I organize company events. The AI-generated invitations are absolutely stunning, and the voting system makes it so easy to find dates that work for everyone."
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-success rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">MR</span>
                        </div>
                        <div>
                          <h4 className="font-semibold">Michael Rodriguez</h4>
                          <p className="text-sm text-muted-foreground">Team Lead</p>
                        </div>
                      </div>
                      <div className="flex mb-3">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Quote className="w-6 h-6 text-muted-foreground mb-3" />
                      <p className="text-base leading-relaxed mb-4">
                        "Finally, no more endless email chains! Our remote team can easily coordinate across time zones, and the mobile app makes it perfect for quick responses on the go."
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="text-left hover:shadow-elevated hover:scale-105 transition-all duration-500 border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-hero rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">EP</span>
                        </div>
                        <div>
                          <h4 className="font-semibold">Emily Parker</h4>
                          <p className="text-sm text-muted-foreground">Wedding Planner</p>
                        </div>
                      </div>
                      <div className="flex mb-3">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Quote className="w-6 h-6 text-muted-foreground mb-3" />
                      <p className="text-base leading-relaxed mb-4">
                        "As a wedding planner, I need tools that work flawlessly. This platform has saved me countless hours coordinating with vendors and clients. The analytics help me understand guest preferences better."
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* How it Works */}
              <div id="how-it-works" className="text-center scroll-mt-20">
                <h2 className="text-3xl sm:text-4xl font-bold mb-12 sm:mb-16 bg-gradient-hero bg-clip-text text-transparent">How It Works</h2>
                <div className="grid md:grid-cols-4 gap-8">
                  {[{
                  icon: Calendar,
                  title: "Create Event",
                  desc: "Set up your event with multiple options",
                  gradient: "bg-gradient-primary"
                }, {
                  icon: Users,
                  title: "Invite Guests",
                  desc: "Share via email, phone, or link",
                  gradient: "bg-gradient-success"
                }, {
                  icon: Vote,
                  title: "Collect Votes",
                  desc: "Guests vote on their preferences",
                  gradient: "bg-gradient-hero"
                }, {
                  icon: MapPin,
                  title: "Finalize Details",
                  desc: "Choose based on majority votes",
                  gradient: "bg-warning"
                }].map((step, index) => <div key={index} className="flex flex-col items-center group">
                      <div className={`w-20 h-20 ${step.gradient} rounded-3xl flex items-center justify-center mb-6 shadow-elevated group-hover:scale-110 transition-all duration-500`}>
                        <step.icon className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="font-bold text-lg mb-3">{step.title}</h3>
                      <p className="text-muted-foreground text-center leading-relaxed">{step.desc}</p>
                      {index < 3 && <div className="hidden md:block absolute top-10 left-full w-8 h-0.5 bg-gradient-to-r from-primary to-transparent"></div>}
                    </div>)}
                </div>
              </div>

              {/* Email Subscription Section */}
              <div className="text-center mt-20">
                <h2 className="text-3xl font-bold mb-4 bg-gradient-hero bg-clip-text text-transparent">
                  🎯 Get Free Resources
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Get our comprehensive event planning guide, plus exclusive tips and early access to new features!
                </p>
                <div className="max-w-lg mx-auto">
                  <EmailCapture 
                    variant="hero"
                    leadMagnet="event_planning_masterclass"
                    source="main_cta"
                    title="Free Event Planning Masterclass"
                    description="Learn the secrets to perfect event coordination and get exclusive templates."
                  />
                </div>
              </div>

              {/* Footer */}
              <footer className="mt-20 border-t border-border/20 pt-12 pb-8">
                <div className="container mx-auto px-4">
                  <div className="grid md:grid-cols-4 gap-8 mb-8">
                    {/* Company Info */}
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-3 mb-4">
                        <img 
                          src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                          alt="EventDatePicker Logo" 
                          className="h-8 w-auto" 
                          loading="lazy"
                        />
                        <span className="text-xl font-bold">EventDatePicker</span>
                      </div>
                      <p className="text-muted-foreground mb-4 max-w-md">
                  The best <strong>free scheduling tool</strong> and <strong>alternative to Doodle</strong> for coordinating events. 
                  <strong>Compare scheduling apps</strong> and discover why we're the top choice for <strong>group event coordination</strong>.
                      </p>
                      <div className="flex gap-4">
                        <Badge variant="secondary" className="text-xs">
                          🌟 Trusted by thousands
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          📱 Mobile optimized
                        </Badge>
                      </div>
                    </div>

                    {/* Quick Links */}
                     <div>
                       <h4 className="font-semibold mb-4">Quick Links</h4>
                       <ul className="space-y-2 text-sm text-muted-foreground">
                         <li><Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/help')}>Help & Documentation</Button></li>
                         <li><Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/contact')}>Contact Us</Button></li>
                         <li><Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/auth')}>Sign Up</Button></li>
                         <li><Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/subscription')}>Pricing</Button></li>
                         {user && (
                           <li><Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/dashboard')}>Dashboard</Button></li>
                         )}
                       </ul>
                     </div>

                     {/* Newsletter Signup */}
                     <div>
                       <h4 className="font-semibold mb-4">Stay Connected</h4>
                       <ul className="space-y-2 text-sm text-muted-foreground">
                         <li className="flex items-center gap-2">
                           <span>📧</span>
                           <Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground" onClick={() => navigate('/contact')}>
                             support@eventdatepicker.com
                           </Button>
                         </li>
                         <li className="flex items-center gap-2">
                           <span>🎯</span>
                           <FeatureRequestDialog>
                             <Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground">
                               Feature Requests
                             </Button>
                           </FeatureRequestDialog>
                         </li>
                       </ul>
                     </div>
                  </div>

                  {/* Bottom Bar */}
                  <div className="border-t border-border/20 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      © 2025 EventDatePicker. All rights reserved.
                    </p>
                    <div className="flex gap-6 text-sm text-muted-foreground">
                      <Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground">Privacy Policy</Button>
                      <Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground">Terms of Service</Button>
                      <Button variant="link" className="p-0 h-auto text-muted-foreground hover:text-foreground">Cookies</Button>
                    </div>
                  </div>
                </div>
              </footer>
            </div>

            {/* Mobile sticky CTA */}
            {!user && (
              <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border px-4 py-3 shadow-elevated">
                <Button
                  variant="hero"
                  onClick={() => navigate('/auth')}
                  className="w-full rounded-xl min-h-[52px] text-base font-semibold"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Create Your Event — Free
                </Button>
              </div>
            )}
            {/* Spacer for mobile sticky CTA */}
            {!user && <div className="md:hidden h-20" aria-hidden="true" />}
          </div>;
    }
  };
  return renderCurrentView();
};
export default Index;